import { sleep } from './sleep';

export interface RetryRequest {
  Result: { response?: Response; error?: unknown };
  ShouldRetry: (
    result: RetryRequest['Result'],
    attempt: number,
    signal: AbortSignal | undefined,
  ) => boolean | Promise<boolean>;
  WaitFor: (attempt: number, signal: AbortSignal | undefined) => Promise<void>;
}

/**
 * Retries request function N times until result should not be retried.
 * By default retries 3 times with 1s exponential backoff until the response is not a server error.
 *
 * @returns The result from last request
 * @see {@link retryRequest.linearWaitFor} For linear backoff
 * @see {@link retryRequest.exponentialWaitFor} For exponential backoff
 */
export async function retryRequest(
  request: (signal?: AbortSignal) => Promise<Response>,
  {
    retries = 3,
    shouldRetry = retryRequest.shouldRetryIf500,
    waitFor = retryRequest.exponentialWaitFor(),
    signal,
  }: {
    retries?: number;
    shouldRetry?: RetryRequest['ShouldRetry'];
    waitFor?: RetryRequest['WaitFor'];
    signal?: AbortSignal;
  } = {},
): Promise<Response> {
  let attempt = 0;

  while (true) {
    signal?.throwIfAborted();

    let response: Response | undefined;
    let error: unknown;

    try {
      response = await request(signal);
    } catch (err) {
      error = err;
    }

    const shouldKeepGoing = await shouldRetry({ response, error }, attempt, signal);

    if (!shouldKeepGoing || attempt >= retries) {
      if (error) {
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw error;
      }

      if (response) {
        return response;
      }

      throw new Error('Retry failed with no response and no error');
    }

    await waitFor(++attempt, signal);
  }
}

const shouldRetryIfNotOk = ({ response, error }: RetryRequest['Result']) =>
  error !== undefined || !response?.ok;

const shouldRetryIf500 = ({ response, error }: RetryRequest['Result']) =>
  error !== undefined || !response?.status || (response.status >= 500 && response.status < 600);

const linearWaitFor =
  (ms = 1000): RetryRequest['WaitFor'] =>
  (_, signal) =>
    sleep(ms, signal);

const exponentialWaitFor =
  (baseMs = 1000): RetryRequest['WaitFor'] =>
  (attempt, signal) =>
    sleep(Math.pow(2, attempt) * baseMs, signal);

retryRequest.shouldRetryIfNotOk = shouldRetryIfNotOk;
retryRequest.shouldRetryIf500 = shouldRetryIf500;
retryRequest.linearWaitFor = linearWaitFor;
retryRequest.exponentialWaitFor = exponentialWaitFor;
